import React from 'react';

import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { FieldHelperProps, useFormikContext } from 'formik';

import { Box } from 'wui/Box';
import { TextArea } from 'wui/TextArea';
import { TextField } from 'wui/TextField';
import { DateTimeField } from 'wui/DateTimeField';
import { ImageViewer } from 'wui/ImageViewer';
import { EditEventFormLayout } from './EditEventFormLayout';

import { DurationField } from './DurationField';
import { IEventForm } from './types';

export function EventForm() {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const form = useFormikContext<IEventForm>();

  return (
    <EditEventFormLayout data-hook="event-form">
      <EditEventFormLayout.LeftColumn>
        <TextField
          id="event-name"
          wired={!isMobile}
          required
          maxLength={50}
          label={t('groups-web.event-form.fields.name.label')}
          placeholder={t('groups-web.event-form.fields.name.placeholder')}
          {...form.getFieldProps('name')}
          error={form.touched.name && form.errors.name}
        />

        <TextArea
          id="event-teaser"
          wired={!isMobile}
          ariaLabel=""
          label={t('groups-web.event-form.fields.teaser.label')}
          placeholder={t('groups-web.event-form.fields.teaser.placeholder')}
          {...form.getFieldProps('teaser')}
          onChange={(event) =>
            handleFieldOnChange(
              form.getFieldHelpers('teaser'),
              event.target.value,
            )
          }
        />

        <DateTimeField
          wired={!isMobile}
          id="event-start-date"
          label={t('groups-web.event-form.fields.start-date.label')}
          required
          {...form.getFieldProps('startDate')}
          error={form.errors.startDate}
          onChange={(newValue: string) =>
            handleFieldOnChange(form.getFieldHelpers('startDate'), newValue)
          }
        />

        <Box direction="vertical" gap="8px">
          <DateTimeField
            wired={!isMobile}
            id="event-end-date"
            label={t('groups-web.event-form.fields.end-date.label')}
            required
            {...form.getFieldProps('endDate')}
            error={form.errors.endDate}
            onChange={(newValue: string) =>
              handleFieldOnChange(form.getFieldHelpers('endDate'), newValue)
            }
          />

          <DurationField
            startDate={new Date(form.values.startDate)}
            endDate={new Date(form.values.endDate)}
            error={form.errors.duration}
          />
        </Box>

        <TextField
          id="event-location"
          wired={!isMobile}
          required
          label={t('groups-web.event-form.fields.location.label')}
          placeholder={t('groups-web.event-form.fields.location.placeholder')}
          {...form.getFieldProps('location')}
          error={form.touched.location && form.errors.location}
        />
      </EditEventFormLayout.LeftColumn>
      <EditEventFormLayout.RightColumn>
        <ImageViewer
          data-hook="event-cover"
          wired={!isMobile}
          {...form.getFieldProps('cover')}
          onChange={(image) => {
            handleFieldOnChange(form.getFieldHelpers('cover'), image);
          }}
          label={t('groups-web.event-form.fields.cover.add.label')}
        />
      </EditEventFormLayout.RightColumn>
    </EditEventFormLayout>
  );

  function handleFieldOnChange<Value>(
    fieldHelpers: FieldHelperProps<Value>,
    newValue: Value,
  ) {
    const { setTouched, setValue } = fieldHelpers;
    setTouched(true);
    setValue(newValue, true);
  }
}

EventForm.displayName = 'EventForm';
